import React, {useEffect, useMemo, useRef, useState} from "react";
import {router, useForm} from "@inertiajs/react";
import {Badge, Button, Card, Container, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import SelectList from "../Components/SelectList.jsx";

// Ab dieser Fensterbreite (Bootstrap "lg") ist der Zeitstrahl waagerecht und
// per Maus verschiebbar - darunter bleibt die bestehende senkrechte
// Darstellung ohne Ziehen (siehe RoadmapTimeline weiter unten).
const ROADMAP_DRAG_BREAKPOINT = 992;

// Technischer Enum-Wert, kein Fließtext - siehe RoadmapItem::HORIZON_ERLEDIGT
// im Backend. Der Horizont "Erreicht" steht im Zeitstrahl immer an erster
// Stelle, direkt links vor "Jetzt" - dort sitzt die "Heute"-Marke.
const ROADMAP_HORIZON_ERLEDIGT = "erledigt";

// Anzeige-Farbe je Status - dieselbe Bootstrap-Palette wie im übrigen
// Backend, keine zusätzliche eigene Farbwelt. Gilt für die Listenansicht
// (Badges); der Zeitstrahl selbst kommt bewusst ohne diese Farben aus, siehe
// dort.
const STATUS_VARIANT = {
    offen: "secondary",
    in_arbeit: "primary",
    erledigt: "success",
};

/**
 * Roadmap der Ordermanagement-Weiterentwicklung.
 *
 * Zwei Ansichten teilen sich dieselben Daten:
 * - Präsentationsansicht (Standard, für alle Berechtigten): Zeitstrahl mit
 *   Pins und Fähnchen, darunter die vollständige Liste zum Nachschlagen -
 *   beides rein lesend.
 * - Bearbeitungsansicht (nur Superadmin): die bisherige Bahn- und
 *   Listendarstellung mit der kompletten Pflege (anlegen, ändern, löschen,
 *   verschieben) - funktional unverändert, sie wandert nur hinter die
 *   Umschaltung.
 *
 * Wer kein Superadmin ist, bekommt den Umschalter gar nicht gerendert und
 * sieht ausschließlich die Präsentationsansicht - das serverseitige
 * `canManage` (siehe RoadmapController) ist deckungsgleich mit
 * `isSuperadmin()` und dient hier zugleich als Schalter-Sichtbarkeit.
 *
 * Sichtbarkeit und Rechte prüft ausschließlich der Server
 * (RoadmapController) - diese Seite bekommt canManage einfach mitgeteilt.
 */
export default function Roadmap({items = [], horizons = [], statuses = [], canManage = false, tickets = []}) {
    // Nur Superadmins dürfen überhaupt umschalten - alle anderen bleiben
    // fest auf der Präsentationsansicht, ganz gleich was lokal im State
    // stünde (kann bei !canManage ohnehin nie auf "edit" wechseln, weil der
    // Umschalter dann nicht gerendert wird).
    const [mode, setMode] = useState("presentation");
    const effectiveMode = canManage ? mode : "presentation";

    const horizonLabel = useMemo(() => {
        const map = {};
        horizons.forEach((h) => { map[h.id] = h.label; });
        return map;
    }, [horizons]);

    const statusLabel = useMemo(() => {
        const map = {};
        statuses.forEach((s) => { map[s.id] = s.label; });
        return map;
    }, [statuses]);

    // Reihenfolge der Zeitstrahl-/Bahn-Abschnitte kommt aus der Reihenfolge
    // der horizons-Prop (siehe RoadmapItem::HORIZON_LABELS) - "laufend"
    // läuft separat als durchgehendes Band, gehört also auf keinen Punkt
    // der Linie.
    const bahnHorizons = horizons.filter((h) => h.id !== "laufend");
    const laufendHorizon = horizons.find((h) => h.id === "laufend");

    const itemsByHorizon = useMemo(() => {
        const map = {};
        items.forEach((item) => {
            (map[item.horizon] ??= []).push(item);
        });
        return map;
    }, [items]);

    return (
        <Container fluid className="py-3 roadmap-page">
            <div className="mb-3 d-flex align-items-start justify-content-between gap-3 flex-wrap">
                <div>
                    <h1 className="mb-0">Roadmap</h1>
                    <small className="text-muted">
                        Weiterentwicklung des Ordermanagements - grob eingeordnet statt fest terminiert.
                    </small>
                </div>

                {/* Umschaltung nur für Superadmin sichtbar - für alle anderen
                    existiert dieser Block im DOM gar nicht erst. */}
                {canManage && (
                    <div className="btn-group btn-group-sm" role="group" aria-label="Ansicht wählen">
                        <Button
                            variant={effectiveMode === "presentation" ? "primary" : "outline-secondary"}
                            onClick={() => setMode("presentation")}
                        >
                            Präsentation
                        </Button>
                        <Button
                            variant={effectiveMode === "edit" ? "primary" : "outline-secondary"}
                            onClick={() => setMode("edit")}
                        >
                            Bearbeitung
                        </Button>
                    </div>
                )}
            </div>

            {effectiveMode === "presentation" ? (
                <RoadmapPresentation
                    bahnHorizons={bahnHorizons}
                    laufendHorizon={laufendHorizon}
                    itemsByHorizon={itemsByHorizon}
                    horizons={horizons}
                    statusLabel={statusLabel}
                    tickets={tickets}
                />
            ) : (
                <RoadmapEditor
                    items={items}
                    horizons={horizons}
                    statuses={statuses}
                    bahnHorizons={bahnHorizons}
                    laufendHorizon={laufendHorizon}
                    itemsByHorizon={itemsByHorizon}
                    statusLabel={statusLabel}
                />
            )}
        </Container>
    );
}

/* -------------------------------------------------------------------- */
/* Präsentationsansicht: Zeitstrahl + vollständige Liste, rein lesend    */
/* -------------------------------------------------------------------- */

function RoadmapPresentation({bahnHorizons, laufendHorizon, itemsByHorizon, horizons, statusLabel, tickets}) {
    const laufendItems = laufendHorizon ? (itemsByHorizon[laufendHorizon.id] || []) : [];

    return (
        <>
            <RoadmapTimeline
                bahnHorizons={bahnHorizons}
                itemsByHorizon={itemsByHorizon}
                statusLabel={statusLabel}
                tickets={tickets}
            />

            {laufendHorizon && laufendItems.length > 0 && (
                <div className="roadmap-laufend-band mb-4">
                    <div className="roadmap-laufend-band-header">
                        {laufendHorizon.label}
                        <span className="roadmap-laufend-band-hint">zieht sich durch alle Phasen</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {laufendItems.map((item) => (
                            <RoadmapCard key={item.id} item={item} statusLabel={statusLabel} compact />
                        ))}
                    </div>
                </div>
            )}

            {/* Nachschlagewerk: dieselben Einträge vollständig ausformuliert,
                ohne jede Pflege-Bedienung. */}
            {horizons.map((horizon) => {
                const horizonItems = itemsByHorizon[horizon.id] || [];
                if (horizonItems.length === 0) {
                    return null;
                }

                return (
                    <section key={horizon.id} className="mb-4">
                        <h2 className="h5">{horizon.label}</h2>
                        <ul className="list-unstyled mb-0">
                            {horizonItems.map((item) => (
                                <RoadmapReadOnlyItem key={item.id} item={item} statusLabel={statusLabel} />
                            ))}
                        </ul>
                    </section>
                );
            })}
        </>
    );
}

function RoadmapReadOnlyItem({item, statusLabel}) {
    const children = item.children || [];

    return (
        <li className="roadmap-list-item mb-3">
            <div className={item.is_major ? "fw-semibold" : ""}>
                {item.title}
                <Badge bg={STATUS_VARIANT[item.status] || "secondary"} className="ms-2">
                    {statusLabel[item.status] || item.status}
                </Badge>
                {item.time_band && <span className="text-muted ms-2 small">{item.time_band}</span>}
            </div>
            {item.description && <div className="text-muted small mt-1">{item.description}</div>}

            {children.length > 0 && (
                <ul className="list-unstyled ms-4 mt-2 mb-0 roadmap-children">
                    {children.map((child) => (
                        <li key={child.id} className="mb-2">
                            <div>
                                {child.title}
                                <Badge bg={STATUS_VARIANT[child.status] || "secondary"} className="ms-2">
                                    {statusLabel[child.status] || child.status}
                                </Badge>
                                {child.time_band && <span className="text-muted ms-2 small">{child.time_band}</span>}
                            </div>
                            {child.description && <div className="text-muted small mt-1">{child.description}</div>}
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}

// Feste Mindestbreite je Abschnitt und ungefährer Platzbedarf pro Pin -
// dieselben Werte tragen sowohl die Linien- als auch die Pin-Zeile, damit
// beide exakt fluchten (siehe RoadmapTimeline). Wächst ein Abschnitt über
// mehrere Pins, wird er entsprechend breiter statt die Fähnchen zu stauchen
// - deshalb wird der Zeitstrahl insgesamt breiter als das Fenster und muss
// verschiebbar sein (siehe roadmap-timeline-scroll).
const ROADMAP_SECTION_MIN_WIDTH = 300;
const ROADMAP_SECTION_WIDTH_PER_PIN = 230;

/**
 * Der Zeitstrahl selbst: eine durchgehende Linie über die volle Breite,
 * unterteilt in die Horizont-Abschnitte "Erreicht", "Jetzt", "Als Nächstes",
 * "Später" und "Perspektive". Jeder Meilenstein sitzt als Pin auf der Linie,
 * mit einem Fähnchen am Ende eines dünnen Stiels; kleine Punkte daneben
 * stehen für zugeordnete Support-Tickets.
 *
 * Die Stiele wechseln je Abschnitt zwischen oben und unten (gerade Position
 * = oben, ungerade = unten) - reine Zeitstrahl-Konvention, damit sich
 * benachbarte Fähnchen nicht überlappen und die typische zackige Silhouette
 * entsteht, die einen Zeitstrahl auf den ersten Blick als solchen erkennbar
 * macht.
 *
 * Der Strahl ist breiter als das Fenster (feste Mindestbreite je Abschnitt)
 * und lässt sich ab der "lg"-Breakpoint-Breite mit der Maus/Stift/Finger
 * ziehen (siehe useRoadmapDragScroll) - darunter bleibt die bisherige
 * senkrechte Stapel-Darstellung ohne Ziehen.
 */
function RoadmapTimeline({bahnHorizons, itemsByHorizon, statusLabel, tickets}) {
    const {scrollRef, onPointerDown, onPointerMove, onPointerUp} = useRoadmapDragScroll();

    // Breite je Abschnitt einmalig berechnen und an beide Zeilen (Linie und
    // Pins) identisch weiterreichen - nur so fluchten sie exakt, obwohl die
    // Breite vom Inhalt (Anzahl Meilensteine) abhängt.
    const sectionWidths = useMemo(() => {
        const widths = {};
        bahnHorizons.forEach((horizon) => {
            const count = Math.max((itemsByHorizon[horizon.id] || []).length, 1);
            widths[horizon.id] = Math.max(ROADMAP_SECTION_MIN_WIDTH, count * ROADMAP_SECTION_WIDTH_PER_PIN);
        });
        return widths;
    }, [bahnHorizons, itemsByHorizon]);

    // Tickets nach ihrem Meilenstein und ihrer Platzierung (vor/bei/nach)
    // gruppieren - einmal für den ganzen Zeitstrahl, nicht pro Pin neu.
    const ticketsByItem = useMemo(() => {
        const map = {};
        (tickets || []).forEach((ticket) => {
            const bucket = (map[ticket.roadmap_item_id] ??= {vor: [], bei: [], nach: []});
            (bucket[ticket.placement] ??= []).push(ticket);
        });
        return map;
    }, [tickets]);

    // "Heute" sitzt exakt am Ende des "Erreicht"-Abschnitts, also links vor
    // "Jetzt" - dieselbe Breite, die auch die Linie/Pin-Zeile für "Erreicht"
    // verwenden, damit die Marke wirklich auf dem Übergang liegt.
    const todayOffset = sectionWidths[ROADMAP_HORIZON_ERLEDIGT] || 0;

    // Gesamtbreite aller Abschnitte zusammen - die Spur (roadmap-timeline-track)
    // bekommt diese Breite explizit gesetzt, damit die absolut positionierte
    // Linie und die Heute-Marke (beide "left/right:0" relativ zur Spur) exakt
    // über die volle Inhaltsbreite reichen, auch wenn die Spur breiter ist
    // als der sichtbare, scrollbare Ausschnitt.
    const totalWidth = useMemo(
        () => bahnHorizons.reduce((sum, horizon) => sum + (sectionWidths[horizon.id] || 0), 0),
        [bahnHorizons, sectionWidths],
    );

    // Beim ersten Anzeigen so weit scrollen, dass die Heute-Marke im linken
    // Drittel sichtbar ist, statt am äußersten Rand zu kleben. Nur oberhalb
    // der Drag-Breakpoint-Breite relevant - darunter ist die Darstellung
    // ohnehin senkrecht gestapelt und nicht horizontal scrollbar.
    useEffect(() => {
        const container = scrollRef.current;
        if (!container || window.innerWidth < ROADMAP_DRAG_BREAKPOINT || todayOffset === 0) {
            return;
        }
        container.scrollLeft = Math.max(0, todayOffset - container.clientWidth / 3);
    }, [todayOffset, scrollRef]);

    return (
        <div className="roadmap-timeline mb-4">
            {/* Scroll-Container: overflow-x ohne sichtbare Leiste (siehe
                app.scss), Verschieben per Maus/Stift/Finger über
                Pointer-Events. Unterhalb der Drag-Breakpoint-Breite greifen
                die Handler gar nicht erst (siehe useRoadmapDragScroll). */}
            <div
                className="roadmap-timeline-scroll"
                ref={scrollRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <div
                    className="roadmap-timeline-track"
                    style={{width: totalWidth, minWidth: totalWidth}}
                >
                    {/* Die Linie ist bewusst vom Pin-Raster getrennt (eigene
                        Zeile), damit beide unabhängig auf derselben
                        Abschnitts-Aufteilung beruhen und exakt fluchten,
                        ohne dass Kartenhöhen die Linienposition verschieben. */}
                    {/* Unterhalb von 992px komplett ausgeblendet (siehe
                        app.scss, media-breakpoint-down(lg)) - die eigene
                        Bootstrap-Utility d-none/d-lg-flex würde hier mit dem
                        unten definierten display:flex derselben Klasse
                        kollidieren (gleiche Spezifität, letzte Regel
                        gewinnt), deshalb steuert allein das projekteigene
                        CSS die Sichtbarkeit. */}
                    <div className="roadmap-timeline-line" aria-hidden="true">
                        {bahnHorizons.map((horizon) => (
                            <div
                                key={horizon.id}
                                className={`roadmap-timeline-segment roadmap-timeline-segment-${horizon.id}`}
                                style={{flex: `0 0 ${sectionWidths[horizon.id]}px`, width: sectionWidths[horizon.id]}}
                            >
                                <span className="roadmap-timeline-segment-label">{horizon.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Senkrechte "Heute"-Marke am Übergang von "Erreicht" zu
                        "Jetzt" - über die volle Höhe des Zeitstrahl-Bereichs,
                        rein dekorativ (der Übergang steht bereits als Text in
                        den Abschnitts-Überschriften). */}
                    {todayOffset > 0 && (
                        <div
                            className="roadmap-timeline-today"
                            style={{left: todayOffset}}
                            aria-hidden="true"
                        >
                            <span className="roadmap-timeline-today-label">Heute</span>
                        </div>
                    )}

                    <div className="roadmap-timeline-pins">
                        {bahnHorizons.map((horizon) => (
                            <div
                                key={horizon.id}
                                // "now" markiert auf schmalen Fenstern (dort ersetzt eine linke
                                // Randlinie die waagerechte Linie oben) den Abschnitt "Jetzt" -
                                // nicht mehr zwangsläufig den ersten Abschnitt, seit "Erreicht"
                                // links davor steht.
                                className={`roadmap-timeline-pin-group${horizon.id === "jetzt" ? " roadmap-timeline-pin-group-now" : ""}`}
                                style={{flex: `0 0 ${sectionWidths[horizon.id]}px`, width: sectionWidths[horizon.id]}}
                            >
                                {/* Unterhalb von 992px ersetzt diese
                                    Beschriftung die waagerechte Linie oben
                                    (die dort ausgeblendet ist) - jeder
                                    Abschnitt bekommt seine eigene, direkt über
                                    seinen Pins stehende Überschrift. */}
                                <div className="roadmap-timeline-mobile-label d-lg-none">{horizon.label}</div>
                                {(itemsByHorizon[horizon.id] || []).map((item, index) => (
                                    <RoadmapTimelinePin
                                        key={item.id}
                                        item={item}
                                        statusLabel={statusLabel}
                                        up={index % 2 === 0}
                                        tickets={ticketsByItem[item.id]}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Verschieben des Zeitstrahls per Maus/Stift/Finger über Pointer-Events
 * (statt separater Maus- und Touch-Handler, damit ein Weg für alle
 * Eingabegeräte reicht). Erst ab einer kleinen Mindestbewegung wird
 * tatsächlich gescrollt/als Ziehen gewertet - ein einfacher Klick auf ein
 * Fähnchen bewegt den Ausschnitt sonst minimal mit, was wie ein Wackler
 * aussähe.
 */
function useRoadmapDragScroll() {
    const scrollRef = useRef(null);
    const dragState = useRef({dragging: false, startX: 0, startScrollLeft: 0});

    const onPointerDown = (event) => {
        const container = scrollRef.current;
        // Unterhalb der Drag-Breakpoint-Breite ist der Zeitstrahl senkrecht
        // gestapelt (siehe app.scss) - dort gibt es nichts zu ziehen.
        if (!container || window.innerWidth < ROADMAP_DRAG_BREAKPOINT) {
            return;
        }
        dragState.current = {dragging: true, startX: event.clientX, startScrollLeft: container.scrollLeft};
        container.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
        const container = scrollRef.current;
        if (!container || !dragState.current.dragging) {
            return;
        }
        const delta = event.clientX - dragState.current.startX;
        // Mindestbewegung von 4px, bevor überhaupt gescrollt wird - ein
        // Klick, der minimal zittert, soll den Ausschnitt nicht verschieben.
        if (Math.abs(delta) < 4) {
            return;
        }
        container.scrollLeft = dragState.current.startScrollLeft - delta;
    };

    const onPointerUp = (event) => {
        const container = scrollRef.current;
        dragState.current.dragging = false;
        if (container && container.hasPointerCapture?.(event.pointerId)) {
            container.releasePointerCapture(event.pointerId);
        }
    };

    return {scrollRef, onPointerDown, onPointerMove, onPointerUp};
}

function RoadmapTimelinePin({item, statusLabel, up, tickets}) {
    const childCount = (item.children || []).length;
    const ticketsVor = tickets?.vor || [];
    const ticketsBei = tickets?.bei || [];
    const ticketsNach = tickets?.nach || [];

    const flag = (
        <div className="roadmap-timeline-flag">
            <div className={`roadmap-timeline-flag-title${item.is_major ? " fw-semibold" : ""}`}>
                {item.title}
            </div>
            {item.time_band && <div className="roadmap-timeline-flag-band">{item.time_band}</div>}
            {item.description && <p className="roadmap-timeline-flag-desc mb-0">{item.description}</p>}
            {childCount > 0 && (
                <span className="roadmap-timeline-flag-count">
                    {childCount} {childCount === 1 ? "Schritt" : "Schritte"}
                </span>
            )}
            {/* Für Screenreader: der Status steckt am Pin nur in Form/Füllung,
                hier deshalb zusätzlich als Text. */}
            <span className="visually-hidden">Status: {statusLabel[item.status] || item.status}</span>
        </div>
    );

    return (
        <div className={`roadmap-timeline-pin roadmap-timeline-pin-${up ? "up" : "down"}`}>
            <div className="roadmap-timeline-pin-slot roadmap-timeline-pin-slot-top">
                {up && (
                    <>
                        {flag}
                        <span className="roadmap-timeline-pin-stem" aria-hidden="true" />
                    </>
                )}
            </div>

            <span className="roadmap-timeline-dot-row">
                {ticketsVor.length > 0 && (
                    <span className="roadmap-timeline-ticket-group">
                        {ticketsVor.map((ticket) => <RoadmapTicketDot key={ticket.id} ticket={ticket} />)}
                    </span>
                )}

                <span
                    className={`roadmap-timeline-pin-dot roadmap-timeline-pin-dot-${item.status}`}
                    aria-hidden="true"
                />

                {ticketsBei.length > 0 && (
                    <span className="roadmap-timeline-ticket-group">
                        {ticketsBei.map((ticket) => <RoadmapTicketDot key={ticket.id} ticket={ticket} />)}
                    </span>
                )}
                {ticketsNach.length > 0 && (
                    <span className="roadmap-timeline-ticket-group roadmap-timeline-ticket-group-nach">
                        {ticketsNach.map((ticket) => <RoadmapTicketDot key={ticket.id} ticket={ticket} />)}
                    </span>
                )}
            </span>

            <div className="roadmap-timeline-pin-slot roadmap-timeline-pin-slot-bottom">
                {!up && (
                    <>
                        <span className="roadmap-timeline-pin-stem" aria-hidden="true" />
                        {flag}
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Kleiner Punkt für ein zugeordnetes Support-Ticket. Deutlich kleiner als
 * der Meilenstein-Pin, damit die Hierarchie auf einen Blick klar bleibt.
 * Details (Titel, Status) zeigt ein selbst gebautes Tooltip beim Überfahren
 * oder Fokussieren - kein natives title-Attribut (im Projekt verboten),
 * dasselbe OverlayTrigger/Tooltip-Muster wie in HelpTooltip.jsx.
 */
function RoadmapTicketDot({ticket}) {
    return (
        <OverlayTrigger
            placement="top"
            flip
            overlay={
                <Tooltip id={`roadmap-ticket-${ticket.id}`} className="roadmap-ticket-tooltip">
                    <div className="roadmap-ticket-tooltip-title">{ticket.title}</div>
                    <div className="roadmap-ticket-tooltip-status">{ticket.status_label}</div>
                </Tooltip>
            }
        >
            <span
                role="img"
                tabIndex={0}
                aria-label={`Ticket: ${ticket.title} (${ticket.status_label})`}
                className="roadmap-timeline-ticket-dot"
            />
        </OverlayTrigger>
    );
}

/* -------------------------------------------------------------------- */
/* Bearbeitungsansicht: bisherige Bahn- und Listendarstellung samt Pflege */
/* -------------------------------------------------------------------- */

function RoadmapEditor({items, horizons, statuses, bahnHorizons, laufendHorizon, itemsByHorizon, statusLabel}) {
    const [showCreateFor, setShowCreateFor] = useState(null); // null | "" (Meilenstein) | parentId (kleiner Punkt)
    const [editingId, setEditingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const milestoneOptions = items.map((item) => ({value: String(item.id), label: item.title}));

    const closeForms = () => {
        setShowCreateFor(null);
        setEditingId(null);
        setConfirmDeleteId(null);
    };

    const requestDelete = (item) => {
        if (confirmDeleteId === item.id) {
            router.delete(`/roadmap/${item.id}`, {preserveScroll: true, onSuccess: () => setConfirmDeleteId(null)});
            return;
        }
        setConfirmDeleteId(item.id);
    };

    const move = (item, direction) => {
        router.post(`/roadmap/${item.id}/move`, {direction}, {preserveScroll: true});
    };

    return (
        <>
            {/* Grafische Bahn */}
            <div className="roadmap-bahn d-flex flex-column flex-lg-row mb-2">
                {bahnHorizons.map((horizon) => (
                    <div key={horizon.id} className="roadmap-bahn-col flex-fill">
                        <div className="roadmap-bahn-col-header">{horizon.label}</div>
                        <div className="d-flex flex-column gap-2">
                            {(itemsByHorizon[horizon.id] || []).map((item) => (
                                <RoadmapCard key={item.id} item={item} statusLabel={statusLabel} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Querschnittsthemen als durchgehendes Band */}
            {laufendHorizon && (itemsByHorizon[laufendHorizon.id] || []).length > 0 && (
                <div className="roadmap-laufend-band mb-4">
                    <div className="roadmap-laufend-band-header">{laufendHorizon.label}</div>
                    <div className="d-flex flex-wrap gap-2">
                        {itemsByHorizon[laufendHorizon.id].map((item) => (
                            <RoadmapCard key={item.id} item={item} statusLabel={statusLabel} compact />
                        ))}
                    </div>
                </div>
            )}

            {showCreateFor === null && (
                <Button
                    variant="outline-primary"
                    size="sm"
                    className="mb-4"
                    onClick={() => setShowCreateFor("")}
                >
                    + Neuer Eintrag
                </Button>
            )}

            {showCreateFor !== null && (
                <Card className="mb-4 shadow-sm">
                    <Card.Body>
                        <RoadmapForm
                            mode="create"
                            defaultParentId={showCreateFor}
                            milestoneOptions={milestoneOptions}
                            items={items}
                            horizons={horizons}
                            statuses={statuses}
                            onCancel={closeForms}
                            onDone={closeForms}
                        />
                    </Card.Body>
                </Card>
            )}

            {/* Listenansicht */}
            {horizons.map((horizon) => {
                const horizonItems = itemsByHorizon[horizon.id] || [];
                if (horizonItems.length === 0) {
                    return null;
                }

                return (
                    <section key={horizon.id} className="mb-4">
                        <h2 className="h5">{horizon.label}</h2>
                        <ul className="list-unstyled mb-0">
                            {horizonItems.map((item, index) => (
                                <RoadmapListItem
                                    key={item.id}
                                    item={item}
                                    isFirst={index === 0}
                                    isLast={index === horizonItems.length - 1}
                                    canManage
                                    editingId={editingId}
                                    setEditingId={setEditingId}
                                    confirmDeleteId={confirmDeleteId}
                                    requestDelete={requestDelete}
                                    cancelDelete={() => setConfirmDeleteId(null)}
                                    move={move}
                                    milestoneOptions={milestoneOptions}
                                    items={items}
                                    horizons={horizons}
                                    statuses={statuses}
                                    statusLabel={statusLabel}
                                    onAddChild={() => setShowCreateFor(String(item.id))}
                                    showAddChildForm={showCreateFor === String(item.id)}
                                    onCloseChildForm={closeForms}
                                />
                            ))}
                        </ul>
                    </section>
                );
            })}
        </>
    );
}

function RoadmapCard({item, statusLabel, compact = false}) {
    return (
        <Card className={`roadmap-card${compact ? " roadmap-card-compact" : ""}`}>
            <Card.Body className="py-2 px-3">
                <div className="fw-semibold small">{item.title}</div>
                {item.time_band && <div className="text-muted" style={{fontSize: "0.75rem"}}>{item.time_band}</div>}
                <Badge bg={STATUS_VARIANT[item.status] || "secondary"} className="mt-1">
                    {statusLabel[item.status] || item.status}
                </Badge>
            </Card.Body>
        </Card>
    );
}

function RoadmapListItem({
    item,
    isFirst,
    isLast,
    canManage,
    editingId,
    setEditingId,
    confirmDeleteId,
    requestDelete,
    cancelDelete,
    move,
    milestoneOptions,
    items,
    horizons,
    statuses,
    statusLabel,
    onAddChild,
    showAddChildForm,
    onCloseChildForm,
}) {
    const isEditing = editingId === item.id;
    const children = item.children || [];

    return (
        <li className="roadmap-list-item mb-3">
            {isEditing ? (
                <Card className="shadow-sm">
                    <Card.Body>
                        <RoadmapForm
                            mode="edit"
                            item={item}
                            milestoneOptions={milestoneOptions}
                            items={items}
                            horizons={horizons}
                            statuses={statuses}
                            onCancel={() => setEditingId(null)}
                            onDone={() => setEditingId(null)}
                        />
                    </Card.Body>
                </Card>
            ) : (
                <div className="d-flex align-items-start justify-content-between gap-3">
                    <div>
                        <div className={item.is_major ? "fw-semibold" : ""}>
                            {item.title}
                            <Badge bg={STATUS_VARIANT[item.status] || "secondary"} className="ms-2">
                                {statusLabel[item.status] || item.status}
                            </Badge>
                            {item.time_band && (
                                <span className="text-muted ms-2 small">{item.time_band}</span>
                            )}
                        </div>
                        {item.description && (
                            <div className="text-muted small mt-1">{item.description}</div>
                        )}
                    </div>

                    {canManage && (
                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <Button
                                variant="light"
                                size="sm"
                                aria-label="Nach oben verschieben"
                                disabled={isFirst}
                                onClick={() => move(item, "up")}
                            >
                                ↑
                            </Button>
                            <Button
                                variant="light"
                                size="sm"
                                aria-label="Nach unten verschieben"
                                disabled={isLast}
                                onClick={() => move(item, "down")}
                            >
                                ↓
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => setEditingId(item.id)}>
                                Bearbeiten
                            </Button>
                            {confirmDeleteId === item.id ? (
                                <>
                                    <span className="text-muted small">Wirklich löschen?</span>
                                    <Button variant="outline-danger" size="sm" onClick={() => requestDelete(item)}>
                                        Ja, löschen
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={cancelDelete}>
                                        Abbrechen
                                    </Button>
                                </>
                            ) : (
                                <Button variant="outline-danger" size="sm" onClick={() => requestDelete(item)}>
                                    Löschen
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Untergeordnete kleine Punkte, eingerückt */}
            {(children.length > 0 || (canManage && !isEditing)) && (
                <ul className="list-unstyled ms-4 mt-2 mb-0 roadmap-children">
                    {children.map((child, index) => (
                        <RoadmapChildItem
                            key={child.id}
                            item={child}
                            isFirst={index === 0}
                            isLast={index === children.length - 1}
                            canManage={canManage}
                            editingId={editingId}
                            setEditingId={setEditingId}
                            confirmDeleteId={confirmDeleteId}
                            requestDelete={requestDelete}
                            cancelDelete={cancelDelete}
                            move={move}
                            milestoneOptions={milestoneOptions}
                            items={items}
                            horizons={horizons}
                            statuses={statuses}
                            statusLabel={statusLabel}
                        />
                    ))}
                    {canManage && !isEditing && (
                        showAddChildForm ? (
                            <li className="mt-2">
                                <Card className="shadow-sm">
                                    <Card.Body>
                                        <RoadmapForm
                                            mode="create"
                                            defaultParentId={String(item.id)}
                                            milestoneOptions={milestoneOptions}
                                            items={items}
                                            horizons={horizons}
                                            statuses={statuses}
                                            onCancel={onCloseChildForm}
                                            onDone={onCloseChildForm}
                                        />
                                    </Card.Body>
                                </Card>
                            </li>
                        ) : (
                            <li className="mt-1">
                                <Button variant="link" size="sm" className="px-0" onClick={onAddChild}>
                                    + Kleinen Punkt hinzufügen
                                </Button>
                            </li>
                        )
                    )}
                </ul>
            )}
        </li>
    );
}

function RoadmapChildItem({
    item,
    isFirst,
    isLast,
    canManage,
    editingId,
    setEditingId,
    confirmDeleteId,
    requestDelete,
    cancelDelete,
    move,
    milestoneOptions,
    items,
    horizons,
    statuses,
    statusLabel,
}) {
    const isEditing = editingId === item.id;

    if (isEditing) {
        return (
            <li className="mb-2">
                <Card className="shadow-sm">
                    <Card.Body>
                        <RoadmapForm
                            mode="edit"
                            item={item}
                            milestoneOptions={milestoneOptions}
                            items={items}
                            horizons={horizons}
                            statuses={statuses}
                            onCancel={() => setEditingId(null)}
                            onDone={() => setEditingId(null)}
                        />
                    </Card.Body>
                </Card>
            </li>
        );
    }

    return (
        <li className="d-flex align-items-start justify-content-between gap-3 mb-2">
            <div>
                <div>
                    {item.title}
                    <Badge bg={STATUS_VARIANT[item.status] || "secondary"} className="ms-2">
                        {statusLabel[item.status] || item.status}
                    </Badge>
                    {item.time_band && <span className="text-muted ms-2 small">{item.time_band}</span>}
                </div>
                {item.description && <div className="text-muted small mt-1">{item.description}</div>}
            </div>

            {canManage && (
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <Button
                        variant="light"
                        size="sm"
                        aria-label="Nach oben verschieben"
                        disabled={isFirst}
                        onClick={() => move(item, "up")}
                    >
                        ↑
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        aria-label="Nach unten verschieben"
                        disabled={isLast}
                        onClick={() => move(item, "down")}
                    >
                        ↓
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setEditingId(item.id)}>
                        Bearbeiten
                    </Button>
                    {confirmDeleteId === item.id ? (
                        <>
                            <span className="text-muted small">Wirklich löschen?</span>
                            <Button variant="outline-danger" size="sm" onClick={() => requestDelete(item)}>
                                Ja, löschen
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={cancelDelete}>
                                Abbrechen
                            </Button>
                        </>
                    ) : (
                        <Button variant="outline-danger" size="sm" onClick={() => requestDelete(item)}>
                            Löschen
                        </Button>
                    )}
                </div>
            )}
        </li>
    );
}

/**
 * Formular für Anlegen und Bearbeiten - bewusst dieselbe Komponente für
 * beide Fälle, damit sich die Felder nicht auseinanderentwickeln. Kein
 * natives <select> (SelectList statt Form.Select) und keine HTML5-
 * Fehlerblasen (noValidate + eigene invalid-feedback-Anzeige über die von
 * Inertia gelieferten "errors").
 */
function RoadmapForm({mode, item, defaultParentId = "", milestoneOptions, items, horizons, statuses, onCancel, onDone}) {
    const isEdit = mode === "edit";

    const {data, setData, post, patch, processing, errors, reset} = useForm({
        title: item?.title || "",
        description: item?.description || "",
        parent_id: item ? (item.parent_id ? String(item.parent_id) : "") : defaultParentId,
        horizon: item?.horizon || (defaultParentId
            ? (items.find((i) => String(i.id) === defaultParentId)?.horizon || horizons[0]?.id || "")
            : horizons[0]?.id || ""),
        time_band: item?.time_band || "",
        status: item?.status || statuses[0]?.id || "offen",
        is_major: item ? item.is_major : defaultParentId === "",
    });

    const parentOptions = [
        {value: "", label: "Kein Elternteil (eigener Meilenstein)"},
        ...milestoneOptions.filter((option) => !isEdit || option.value !== String(item.id)),
    ];

    const horizonOptions = horizons.map((h) => ({value: h.id, label: h.label}));
    const statusOptions = statuses.map((s) => ({value: s.id, label: s.label}));

    const handleParentChange = (value) => {
        setData((current) => {
            const parentHorizon = value ? items.find((i) => String(i.id) === value)?.horizon : null;
            return {
                ...current,
                parent_id: value,
                horizon: parentHorizon || current.horizon,
                is_major: value ? false : current.is_major,
            };
        });
    };

    const submit = (event) => {
        event.preventDefault();
        const options = {preserveScroll: true, onSuccess: () => { reset(); onDone(); }};

        if (isEdit) {
            patch(`/roadmap/${item.id}`, options);
        } else {
            post("/roadmap", options);
        }
    };

    const hasParent = data.parent_id !== "";

    return (
        <Form noValidate onSubmit={submit}>
            <Form.Group className="mb-2">
                <Form.Label>Titel</Form.Label>
                <Form.Control
                    value={data.title}
                    onChange={(e) => setData("title", e.target.value)}
                    isInvalid={!!errors.title}
                    autoFocus
                />
                {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
            </Form.Group>

            <Form.Group className="mb-2">
                <Form.Label>Beschreibung</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    value={data.description}
                    onChange={(e) => setData("description", e.target.value)}
                    isInvalid={!!errors.description}
                />
                {errors.description && <div className="invalid-feedback d-block">{errors.description}</div>}
            </Form.Group>

            <div className="row g-2 mb-2">
                <div className="col-sm-6">
                    <Form.Label>Elternteil</Form.Label>
                    <SelectList
                        value={data.parent_id}
                        options={parentOptions}
                        onChange={handleParentChange}
                        ariaLabel="Elternteil"
                    />
                </div>
                <div className="col-sm-6">
                    <Form.Label>Horizont</Form.Label>
                    {hasParent ? (
                        <div className="form-control-plaintext small text-muted">
                            {horizons.find((h) => h.id === data.horizon)?.label || data.horizon} (vom Elternteil übernommen)
                        </div>
                    ) : (
                        <SelectList
                            value={data.horizon}
                            options={horizonOptions}
                            onChange={(value) => setData("horizon", value)}
                            ariaLabel="Horizont"
                        />
                    )}
                    {errors.horizon && <div className="invalid-feedback d-block">{errors.horizon}</div>}
                </div>
            </div>

            <div className="row g-2 mb-2">
                <div className="col-sm-6">
                    <Form.Label>Zeitband (frei, optional)</Form.Label>
                    <Form.Control
                        placeholder="z. B. 2. Halbjahr 2026"
                        value={data.time_band}
                        onChange={(e) => setData("time_band", e.target.value)}
                        isInvalid={!!errors.time_band}
                    />
                    {errors.time_band && <div className="invalid-feedback d-block">{errors.time_band}</div>}
                </div>
                <div className="col-sm-6">
                    <Form.Label>Status</Form.Label>
                    <SelectList
                        value={data.status}
                        options={statusOptions}
                        onChange={(value) => setData("status", value)}
                        ariaLabel="Status"
                    />
                </div>
            </div>

            {!hasParent && (
                <Form.Check
                    type="checkbox"
                    id={`roadmap-is-major-${item?.id || "new"}`}
                    className="mb-3"
                    label="Großer Meilenstein (fett in der Liste, eigene Karte in der Bahn)"
                    checked={data.is_major}
                    onChange={(e) => setData("is_major", e.target.checked)}
                />
            )}

            <div className="d-flex gap-2">
                <Button type="submit" size="sm" disabled={processing}>
                    {isEdit ? "Speichern" : "Anlegen"}
                </Button>
                <Button type="button" variant="light" size="sm" onClick={onCancel}>
                    Abbrechen
                </Button>
            </div>
        </Form>
    );
}
